/**
 * 프론트 크래시·청크 로드 실패·처리되지 않은 예외·API 네트워크 실패를 서버(`POST /api/log/client-error`)로 보낸다.
 *
 * 배경(2026-09 유저 노출 오류 감사): ErrorBoundary는 console.error만 하고, 청크 실패·fetch 실패는 어디에도
 * 남지 않아 프론트 오류가 서버 로그에서 완전한 사각지대였다. 이 모듈은 그 네 종류만 최소 정보로 수집한다.
 *
 * 원칙
 * - 절대 throw하지 않고, 사용자 흐름을 막지 않는다(fire-and-forget, keepalive).
 * - 개인정보를 보내지 않는다: URL은 경로만(쿼리·해시 제거), 메시지·스택은 길이 제한, uid는 있으면 그대로(서버 로그 규칙과 동일).
 * - 같은 (event_type, 메시지 앞 120자)는 세션에서 1분에 한 번만, 세션 전체 20건까지만 보낸다(폭주 방지).
 * - 소음 제외: "Script error."(교차 출처 무정보), ResizeObserver 루프, 브라우저 확장 URL, 사용자 취소(AbortError).
 */

import { getApiUrl } from './apiConfig';

export const CLIENT_ERROR_EVENT = {
  API_NETWORK_ERROR: 'API_NETWORK_ERROR',
  CHUNK_LOAD_ERROR: 'CHUNK_LOAD_ERROR',
  UI_CRASH: 'UI_CRASH',
  UNHANDLED_ERROR: 'UNHANDLED_ERROR',
  UNHANDLED_REJECTION: 'UNHANDLED_REJECTION',
};

const ENDPOINT = '/api/log/client-error';
const MAX_DETAILS_CHARS = 1500;
const MAX_STACK_LINES = 8;
const DEDUP_WINDOW_MS = 60_000;
const SESSION_MAX_REPORTS = 20;
const NOISE_RE = /^Script error\.?$|ResizeObserver loop|Non-Error promise rejection captured with value: undefined/i;
const EXTENSION_URL_RE = /^(chrome|moz|safari)-extension:\/\//i;

const state = {
  disabled: false,
  lastSentAt: new Map(),
  sent: 0,
  // 테스트에서 fetch를 바꿔 끼울 수 있게 늦게 바인딩한다
  transport: null,
  // 현재 로그인 uid를 돌려주는 함수. index.js가 firebase auth로 등록한다(이 모듈은 firebase를 직접 import하지 않는다).
  userProvider: null,
};

export const setClientErrorUserProvider = (provider) => {
  state.userProvider = typeof provider === 'function' ? provider : null;
};

export const isChunkLoadError = (error) => {
  if (!error) return false;
  const name = String(error.name || '');
  const message = String(error.message || error || '');
  return name === 'ChunkLoadError' || /Loading (CSS )?chunk [\w-]+ failed/i.test(message);
};

const safePath = (value) => {
  try {
    const url = new URL(String(value || ''), typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    return url.pathname.slice(0, 200);
  } catch {
    return String(value || '').split('?')[0].slice(0, 200);
  }
};

const trimStack = (stack) => String(stack || '')
  .split('\n')
  .slice(0, MAX_STACK_LINES)
  .map((line) => line.trim())
  .join('\n');

export const describeError = (error) => {
  if (error == null) return { message: '', name: '', stack: '' };
  if (typeof error === 'string') return { message: error, name: 'String', stack: '' };
  return {
    message: String(error.message || error.reason || error.toString?.() || ''),
    name: String(error.name || (error.constructor && error.constructor.name) || 'Error'),
    stack: trimStack(error.stack),
  };
};

const currentUid = () => {
  try {
    const uid = state.userProvider ? state.userProvider() : null;
    return uid ? String(uid) : null;
  } catch {
    return null;
  }
};

const baseExtra = () => {
  if (typeof window === 'undefined') return {};
  return {
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    path: safePath(window.location.href),
    user_agent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '').slice(0, 200) : undefined,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
};

const shouldSend = (eventType, message) => {
  if (state.disabled) return false;
  if (state.sent >= SESSION_MAX_REPORTS) return false;
  const key = `${eventType}|${String(message || '').slice(0, 120)}`;
  const now = Date.now();
  const last = state.lastSentAt.get(key) || 0;
  if (now - last < DEDUP_WINDOW_MS) return false;
  state.lastSentAt.set(key, now);
  return true;
};

/**
 * 서버로 한 건 보낸다. 실패해도 조용히 끝난다.
 * @param {string} eventType CLIENT_ERROR_EVENT 중 하나
 * @param {{ details?: string, extra?: object, userUid?: string|null }} payload
 * @returns {boolean} 실제로 전송을 시도했는지(테스트·디버깅용)
 */
export const reportClientError = (eventType, { details = '', extra = {}, userUid } = {}) => {
  try {
    if (!shouldSend(eventType, details)) return false;
    state.sent += 1;
    const body = JSON.stringify({
      event_type: eventType,
      user_uid: userUid === undefined ? currentUid() : userUid,
      details: String(details || '').slice(0, MAX_DETAILS_CHARS),
      extra: { ...baseExtra(), ...extra },
    });
    const send = state.transport || (typeof fetch === 'function' ? fetch : null);
    if (!send) return false;
    const promise = send(getApiUrl(ENDPOINT), {
      body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      method: 'POST',
    });
    if (promise && typeof promise.catch === 'function') promise.catch(() => {});
    return true;
  } catch {
    return false;
  }
};

/** 렌더 중 예외(ErrorBoundary.componentDidCatch). 청크 실패는 별도 타입으로 구분한다. */
export const reportUiCrash = (error, info) => {
  const { message, name, stack } = describeError(error);
  const componentStack = trimStack(info?.componentStack);
  return reportClientError(
    isChunkLoadError(error) ? CLIENT_ERROR_EVENT.CHUNK_LOAD_ERROR : CLIENT_ERROR_EVENT.UI_CRASH,
    {
      details: `${name}: ${message}\n${stack}${componentStack ? `\n--component--\n${componentStack}` : ''}`,
      extra: { error_name: name },
    },
  );
};

/**
 * fetchWithRetry가 재시도를 다 쓰고도 실패했을 때. 서버가 5xx를 돌려준 경우는 서버 로그에 이미 있으니 보내지 않고,
 * 네트워크 단절·타임아웃처럼 서버에 흔적이 없는 실패만 보낸다.
 */
export const reportApiNetworkError = ({ url, method, attempts, kind, error }) => {
  const { message, name } = describeError(error);
  return reportClientError(CLIENT_ERROR_EVENT.API_NETWORK_ERROR, {
    details: `${kind} ${method || 'GET'} ${safePath(url)} (${name}: ${message})`,
    extra: { api_path: safePath(url), attempts, error_name: name, kind, method: method || 'GET' },
  });
};

const isNoise = (error, source) => {
  const { message } = describeError(error);
  if (!message) return true;
  if (NOISE_RE.test(message)) return true;
  if (source && EXTENSION_URL_RE.test(String(source))) return true;
  if (error && String(error.name || '') === 'AbortError') return true;
  return false;
};

/**
 * window 'error' / 'unhandledrejection' 리스너를 한 번만 단다. index.js에서 호출한다.
 * @returns {() => void} 해제 함수
 */
export const installGlobalErrorReporting = ({ target } = {}) => {
  const win = target || (typeof window !== 'undefined' ? window : null);
  if (!win || win.__WQ_ERROR_REPORTING_INSTALLED__) return () => {};
  win.__WQ_ERROR_REPORTING_INSTALLED__ = true;

  const onError = (event) => {
    const error = event?.error || new Error(String(event?.message || ''));
    if (isNoise(error, event?.filename)) return;
    const { message, name, stack } = describeError(error);
    reportClientError(
      isChunkLoadError(error) ? CLIENT_ERROR_EVENT.CHUNK_LOAD_ERROR : CLIENT_ERROR_EVENT.UNHANDLED_ERROR,
      {
        details: `${name}: ${message}\n${stack}`,
        extra: { error_name: name, source: safePath(event?.filename), line: event?.lineno, col: event?.colno },
      },
    );
  };

  const onRejection = (event) => {
    const reason = event?.reason;
    if (isNoise(reason)) return;
    const { message, name, stack } = describeError(reason);
    reportClientError(
      isChunkLoadError(reason) ? CLIENT_ERROR_EVENT.CHUNK_LOAD_ERROR : CLIENT_ERROR_EVENT.UNHANDLED_REJECTION,
      { details: `${name}: ${message}\n${stack}`, extra: { error_name: name } },
    );
  };

  win.addEventListener('error', onError);
  win.addEventListener('unhandledrejection', onRejection);
  return () => {
    win.removeEventListener('error', onError);
    win.removeEventListener('unhandledrejection', onRejection);
    win.__WQ_ERROR_REPORTING_INSTALLED__ = false;
  };
};

/** 테스트 전용: 전송 함수 교체·상태 초기화 */
export const __resetClientErrorReporterForTests = ({ transport = null, disabled = false } = {}) => {
  state.disabled = disabled;
  state.lastSentAt = new Map();
  state.sent = 0;
  state.transport = transport;
  state.userProvider = null;
};
